import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiLoading, mdiRefresh, mdiRouterWireless, mdiPencilOutline } from '@mdi/js';
import type { AgenteDTO } from '@webip/shared/src/dto/agente';

/** Espelha MndpCandidate do backend (agent-ws-service.ts). */
type MndpCandidate = {
    mac: string;
    identity: string;
    version?: string;
    board?: string;
    ipv4?: string;
};

type DiscoverResult = {
    ok: boolean;
    candidates: MndpCandidate[];
};

export type AdoptionPrefill = {
    agentId: string;
    vendor: 'mikrotik' | 'opnsense';
    endereco?: string;
    nome?: string;
};

/** Identity de fábrica — RouterOS nunca configurado ainda não vale como "nome". */
const FACTORY_IDENTITY = 'MikroTik';

type Props = {
    agente: AgenteDTO | null;
    onClose: () => void;
    onEscolher: (prefill: AdoptionPrefill) => void;
};

/**
 * Adoção de um agente que já está conectado e ainda não tem appliance.
 *
 * É a segunda metade do fluxo de instalação, separada dele de propósito: o
 * agente pode ficar dias esperando adoção (instalado pelo técnico em campo,
 * cadastrado pelo escritório depois), e a modal de instalação — que vive num
 * localStorage de um navegador específico — não é lugar pra esse estado.
 * O que a tela de agentes lista veio do backend, então qualquer pessoa, em
 * qualquer máquina, retoma daqui.
 */
const AdotarAgenteModal = ({ agente, onClose, onEscolher }: Props) => {
    const agentId = agente?.agent_id;
    const vendor = agente?.agent_type ?? 'mikrotik';

    // Descoberta só existe pra MikroTik (MNDP é protocolo proprietário deles).
    const queryDiscover = useQuery<DiscoverResult>({
        queryKey: ['mndp-discover', agentId],
        queryFn: async () => {
            const res = await axios.get<DiscoverResult>(`/api/enrollment_token/${agentId}/discover`, { withCredentials: true });
            return res.data;
        },
        enabled: !!agentId && vendor === 'mikrotik' && !!agente?.online,
        refetchInterval: 5_000,
    });

    return (
        <Modal
            show={agente !== null}
            title={agente ? `Adotar agente ${agente.agent_id}` : 'Adotar agente'}
            onClose={onClose}
            hideControls
            size="md"
        >
            <VBox style={{ gap: '16px', minWidth: '420px' }}>
                {agente && !agente.online && (
                    <AlertBox variant="warning">
                        Este agente está offline agora. Dá pra cadastrar o equipamento manualmente, mas a descoberta
                        automática só funciona com o agente conectado.
                    </AlertBox>
                )}

                {vendor === 'mikrotik' && agente?.online ? (
                    <VBox style={{ gap: '10px' }}>
                        <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>Equipamentos vistos na rede</span>
                            <Botao
                                variant="none-secondary"
                                icon={queryDiscover.isFetching ? mdiLoading : mdiRefresh}
                                onClick={() => queryDiscover.refetch()}
                                disabled={queryDiscover.isFetching}
                            >
                                {queryDiscover.isFetching ? '' : 'Atualizar'}
                            </Botao>
                        </HBox>

                        {!queryDiscover.data && queryDiscover.isLoading && (
                            <HBox style={{ gap: '8px', alignItems: 'center', color: 'var(--tc-color-gray-600)', fontSize: '13px' }}>
                                <Icon path={mdiLoading} spin size="16px" />
                                Procurando roteadores MikroTik na rede local…
                            </HBox>
                        )}

                        {queryDiscover.data?.candidates.length === 0 && (
                            <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                                Nenhum equipamento apareceu ainda. O roteador anuncia a cada alguns segundos — clique em "Atualizar" ou cadastre manualmente abaixo.
                            </span>
                        )}

                        {(queryDiscover.data?.candidates.length ?? 0) > 0 && (
                            <VBox style={{ gap: '6px' }}>
                                {queryDiscover.data!.candidates.map((cand) => (
                                    <HBox
                                        key={cand.mac}
                                        style={{
                                            justifyContent: 'space-between', alignItems: 'center', gap: '10px',
                                            padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--tc-color-gray-200)',
                                        }}
                                    >
                                        <HBox style={{ gap: '10px', alignItems: 'center', minWidth: 0 }}>
                                            <Icon path={mdiRouterWireless} size="20px" />
                                            <VBox style={{ gap: '1px', minWidth: 0 }}>
                                                <strong style={{ fontSize: '13px' }}>
                                                    {cand.identity === FACTORY_IDENTITY ? 'Roteador não configurado' : cand.identity}
                                                    {cand.board ? ` — ${cand.board}` : ''}
                                                </strong>
                                                <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                                    {cand.ipv4 ?? 'IP não capturado'} · {cand.mac}{cand.version ? ` · RouterOS ${cand.version}` : ''}
                                                </span>
                                            </VBox>
                                        </HBox>
                                        <Botao
                                            variant="primary"
                                            size="sm"
                                            onClick={() => onEscolher({
                                                agentId: agentId!,
                                                vendor,
                                                endereco: cand.ipv4,
                                                nome: cand.board || (cand.identity !== FACTORY_IDENTITY ? cand.identity : undefined),
                                            })}
                                        >
                                            Adotar
                                        </Botao>
                                    </HBox>
                                ))}
                            </VBox>
                        )}
                    </VBox>
                ) : (
                    vendor !== 'mikrotik' && (
                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--tc-color-gray-600)' }}>
                            Descoberta automática só existe pra MikroTik por enquanto — cadastre o OPNsense manualmente.
                        </p>
                    )
                )}

                <HBox style={{ justifyContent: 'flex-end' }}>
                    <Botao
                        variant="none-secondary"
                        icon={mdiPencilOutline}
                        onClick={() => onEscolher({ agentId: agentId!, vendor })}
                    >
                        Cadastrar equipamento
                    </Botao>
                </HBox>
            </VBox>
        </Modal>
    );
};

export default AdotarAgenteModal;
